import {formatDate} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {
  adbAlpha,
  adbCategoryAxis,
  adbChartTheme,
  adbGrid,
  adbLegend,
  adbRedrawOnThemeChange,
  adbTooltip,
  adbValueAxis,
  adbZoom
} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {AdminDashboardReportBlock, AdminDashboardReportProvider} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {
  AdminDashboardQualityPoint,
  AdminDashboardQueryParams,
  AdminDashboardWidgetState,
  AdminDashboardXatBotQuality
} from '@features/admin-panel/models/admin-dashboard.models';

/** The ECharts entry point behind a plain object, so a spec can stub it (`spyOn(echarts, 'init')` throws). */
export const xatbotQualityCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const int = new Intl.NumberFormat('en-US', {maximumFractionDigits: 0});

/** "1 Oct" for a UTC ISO date. */
export function qualityDayLabel(iso: string): string {
  const date = new Date(iso);
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
}

/** "▲ 25.0%" / "▼ 10.0%" against the previous value; "no comparison" without one. */
export function qualityChangeText(current: number, previous: number): string {
  if (!previous) {
    return 'no comparison';
  }
  const change = (current - previous) / previous;
  return `${change >= 0 ? '▲' : '▼'} ${(Math.abs(change) * 100).toFixed(1)}%`;
}

/**
 * Product quality: XatBot's unresolved share (line, left axis) and negative ratings (bars, right axis) per day, or per
 * week over 12 months, with the period totals against the previous period. Reloads whenever the dashboard's params
 * change; the chart is created only when there are questions to draw.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-xatbot-quality',
  templateUrl: './admin-dashboard-xatbot-quality.component.html',
  styleUrls: ['./admin-dashboard-xatbot-quality.component.scss']
})
export class AdminDashboardXatBotQualityComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() params: AdminDashboardQueryParams;
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  quality: AdminDashboardXatBotQuality | null = null;
  /** False until the first response. */
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;
  readonly failedTitle = 'Couldn\'t load XatBot unresolved and negative feedback';

  private chartElement: HTMLDivElement;
  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry,
    private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('product-quality', this.host.nativeElement, this);
  }

  @ViewChild('chart') set chartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.chartElement) {
      return;
    }
    this.disposeChart();
    if (container) {
      this.chartElement = container.nativeElement;
      this.chart = xatbotQualityCharts.init(this.chartElement);
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(this.chartElement);
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.disposeChart();
    this.reportRegistry.unregister(this);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-11). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    const title = 'XatBot unresolved and negative feedback';
    if (this.failed) {
      return [{kind: 'empty', title, text: this.failedTitle}];
    }
    const block: AdminDashboardReportBlock = this.hasQuestions
      ? {
        kind: 'chart',
        title,
        subtitle: `${this.rateText}${this.points !== null ? ` · ${this.pointsText}` : ''} · ${this.negativeText}`,
        image: this.chart ? this.chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF', excludeComponents: ['dataZoom']}) : null,
        widthPx: this.chart?.getWidth() ?? 0,
        heightPx: this.chart?.getHeight() ?? 0
      }
      : {kind: 'empty', title, text: 'No XatBot questions in this view'};
    if (this.quality?.historyFrom) {
      block.note = `Collecting history — trend available from ${formatDate(this.quality.historyFrom, 'd MMM y', 'en-US', 'UTC')}`;
    }
    return [block];
  }

  get hasQuestions(): boolean {
    return (this.quality?.questions ?? 0) > 0;
  }

  /** "16.0% of 1,250 questions unresolved" */
  get rateText(): string {
    return `${((this.quality?.unresolvedRate ?? 0) * 100).toFixed(1)}% of ${countOf(this.quality?.questions ?? 0, 'question')} unresolved`;
  }

  /** The change of the unresolved share in percentage points; null when either period has no questions. */
  get points(): number | null {
    const quality = this.quality;
    if (!quality || quality.unresolvedRate === null || quality.previousUnresolvedRate === null) {
      return null;
    }
    return (quality.unresolvedRate - quality.previousUnresolvedRate) * 100;
  }

  /** "▲ 2.0 pts" */
  get pointsText(): string {
    const points = this.points ?? 0;
    return `${points >= 0 ? '▲' : '▼'} ${Math.abs(points).toFixed(1)} pts`;
  }

  /** "30 negative ratings (▲ 25.0%)." */
  get negativeText(): string {
    const negative = this.quality?.negative ?? 0;
    return `${countOf(negative, 'negative rating')} (${qualityChangeText(negative, this.quality?.previousNegative ?? 0)}).`;
  }

  /** "16.0% unresolved · 4 negative ratings", or "No questions · 4 negative ratings". */
  tooltipText(point: AdminDashboardQualityPoint): string {
    const rate = point?.unresolvedRate;
    const share = rate === null || rate === undefined ? 'No questions' : `${(rate * 100).toFixed(1)}% unresolved`;
    return `${share} · ${countOf(point?.negative ?? 0, 'negative rating')}`;
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getXatBotQuality(this.params).subscribe({
      next: quality => this.show(quality ?? null, false),
      error: () => this.show(null, true)
    });
  }

  private show(quality: AdminDashboardXatBotQuality | null, failed: boolean): void {
    this.quality = quality;
    this.failed = failed;
    this.loaded = true;
    this.render();
    this.report(failed ? 'failed' : this.hasQuestions ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }

  private disposeChart(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
    this.chartElement = null;
  }

  /**
   * The OCA-E-159 quality chart (M/charts.ts qualityOption): negative ratings as purple bars at 45% on the right axis,
   * the unresolved share as the red line on the left axis, the legend top right, zoom inside and on a slider.
   */
  private render(): void {
    if (!this.chart || !this.quality) {
      return;
    }
    const quality = this.quality;
    const points = quality.points ?? [];
    const labels = points.map(p => quality.bucket === 'week' ? `w/c ${qualityDayLabel(p.date)}` : qualityDayLabel(p.date));
    const t = adbChartTheme(this.host.nativeElement);
    const option: echarts.EChartsOption = {
      grid: adbGrid({top: 30, bottom: 52}),
      legend: {...adbLegend(t), data: ['Unresolved %', 'Negative feedback']},
      xAxis: adbCategoryAxis(t, labels),
      yAxis: [
        adbValueAxis(t, (value: number) => `${Math.round(value * 100)}%`, {min: 0}),
        adbValueAxis(t, (value: number) => int.format(value), {min: 0, splitLine: {show: false}})
      ],
      dataZoom: adbZoom(t),
      series: [
        {
          name: 'Negative feedback',
          type: 'bar',
          yAxisIndex: 1,
          data: points.map(p => p.negative),
          barMaxWidth: 10,
          itemStyle: {color: adbAlpha(t.purple, 0.45), borderRadius: [2, 2, 0, 0]}
        },
        {
          name: 'Unresolved %',
          type: 'line',
          data: points.map(p => p.unresolvedRate),
          showSymbol: false,
          connectNulls: true,
          lineStyle: {width: 2, color: t.red},
          itemStyle: {color: t.red}
        }
      ],
      tooltip: adbTooltip(t, {
        formatter: (params: any) => {
          const point = Array.isArray(params) ? params[0] : params;
          return `<b>${point?.name}</b><br/>${this.tooltipText(points[point?.dataIndex])}`;
        }
      })
    };
    this.chart.setOption(option, true);
  }
}
